import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Mail, TriangleAlert } from 'lucide-react';
import { RequireRole } from '../components/RequireRole';
import { RoleBadge, StatusBadge } from '../components/RoleBadge';
import { ConfirmErase } from '../components/ConfirmErase';
import { inputClass } from '../components/fields';
import {
  changeRole,
  changeStatus,
  eraseMember,
  getMember,
  resendWelcome,
  type MemberDetail as MemberDetailData,
} from '../lib/members';
import type { Me } from '../lib/session';

type Load = 'loading' | 'ready' | 'not_found' | 'error';
type Notice = { tone: 'success' | 'error'; text: string } | null;

/**
 * Spec 004 — member detail (SC-414/415/417/421). Board+ see full detail; the "Administration" section
 * (role change, enable/disable, erase-on-behalf) renders only for admins — matching the server, which is
 * the real authority. Every governance action surfaces a specific outcome, including the last-admin
 * refusal. Four states: loading → detail → not found (missing/erased) → error. WCAG 2.1 AA.
 */
function MemberDetailInner({ viewer }: { viewer: Me }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>('loading');
  const [m, setM] = useState<MemberDetailData | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const [roleDraft, setRoleDraft] = useState('');
  const [showErase, setShowErase] = useState(false);

  const isAdmin = viewer.role === 'admin';

  const refresh = useCallback(async () => {
    const r = await getMember(id);
    if (r.kind === 'ok') {
      setM(r.data);
      setRoleDraft(r.data.role);
      setLoad('ready');
    } else if (r.kind === 'not_found') {
      setLoad('not_found');
    } else if (r.kind === 'forbidden') {
      setLoad('error');
    } else {
      setLoad('error');
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function saveRole() {
    if (!m || busy || roleDraft === m.role) return;
    setBusy(true);
    setNotice(null);
    const r = await changeRole(m.accountId, roleDraft);
    setBusy(false);
    if (r.kind === 'ok') {
      setNotice({ tone: 'success', text: `Role changed to ${roleDraft}.` });
      void refresh();
    } else if (r.kind === 'conflict') {
      setNotice({ tone: 'error', text: r.message });
      setRoleDraft(m.role);
    } else {
      setNotice({ tone: 'error', text: 'We couldn’t change the role. Please try again.' });
    }
  }

  async function onResendWelcome() {
    if (!m || busy) return;
    setBusy(true);
    setNotice(null);
    const r = await resendWelcome(m.id);
    setBusy(false);
    if (r.kind === 'ok') {
      setNotice({
        tone: r.data.emailSent ? 'success' : 'error',
        text: r.data.emailSent ? 'Welcome email sent.' : 'The welcome email still couldn’t be sent. Check the mail settings.',
      });
    } else if (r.kind === 'not_found' || r.kind === 'conflict') {
      setNotice({ tone: 'error', text: r.message });
    } else {
      setNotice({ tone: 'error', text: 'We couldn’t resend the welcome email. Please try again.' });
    }
  }

  async function toggleStatus() {
    if (!m || busy) return;
    const next = m.status === 'active' ? 'disabled' : 'active';
    setBusy(true);
    setNotice(null);
    const r = await changeStatus(m.accountId, next);
    setBusy(false);
    if (r.kind === 'ok') {
      setNotice({ tone: 'success', text: next === 'disabled' ? 'Account disabled. Its sessions were ended.' : 'Account re-enabled.' });
      void refresh();
    } else if (r.kind === 'conflict') {
      setNotice({ tone: 'error', text: r.message });
    } else {
      setNotice({ tone: 'error', text: 'We couldn’t change the account status. Please try again.' });
    }
  }

  if (load === 'loading') {
    return (
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-6 py-24" aria-live="polite">
        <Loader2 aria-hidden className="h-8 w-8 animate-spin text-accent" />
        <p className="mt-4 font-serif text-lg text-ink">Loading member…</p>
      </div>
    );
  }

  if (load === 'not_found') {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-24 text-center">
        <h1 className="font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">Member not found</h1>
        <p className="mt-3 font-serif text-lg text-muted">This member no longer exists, or has been erased.</p>
        <Link to="/members" className="mt-6 inline-block font-sans text-sm text-accent-deep underline underline-offset-4 hover:text-ink">Back to the register</Link>
      </div>
    );
  }

  if (load === 'error' || !m) {
    return (
      <div className="mx-auto w-full max-w-2xl px-6 py-24">
        <p role="alert" className="flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          We couldn’t load this member. Please refresh and try again.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-6 py-16 sm:py-20">
      <Link to="/members" className="font-sans text-sm text-accent-deep underline underline-offset-4 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
        ← Member register
      </Link>
      <h1 className="mt-4 font-display text-3xl font-semibold tracking-wide text-ink sm:text-4xl">{m.displayName}</h1>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <RoleBadge role={m.role} />
        <StatusBadge status={m.status} />
      </div>

      {notice && (
        <p
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className={`mt-6 flex items-start gap-2 rounded-md border px-3 py-2 font-sans text-sm ${
            notice.tone === 'error' ? 'border-error/40 bg-error/5 text-error' : 'border-success/40 bg-success/5 text-success'
          }`}
        >
          {notice.tone === 'error' ? <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" /> : <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />}
          {notice.text}
        </p>
      )}

      <dl className="mt-8 grid grid-cols-[auto,1fr] gap-x-6 gap-y-2 font-sans text-sm">
        <dt className="text-muted">Email</dt>
        <dd className="break-all text-ink">{m.email}</dd>
        <dt className="text-muted">Joined</dt>
        <dd className="text-ink">{new Date(m.joinedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</dd>
        {m.pronouns && (<><dt className="text-muted">Pronouns</dt><dd className="text-ink">{m.pronouns}</dd></>)}
        <dt className="text-muted">Consent</dt>
        <dd className="text-ink">version {m.consentVersion}</dd>
      </dl>
      {m.bio && <p className="mt-4 whitespace-pre-wrap font-serif text-base leading-relaxed text-ink">{m.bio}</p>}

      <div className="mt-6">
        <button
          type="button"
          onClick={onResendWelcome}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3.5 py-2 font-sans text-sm font-medium text-ink transition-colors duration-150 ease-standard hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
        >
          <Mail aria-hidden className="h-4 w-4" />
          Resend welcome email
        </button>
        <p className="mt-1.5 font-sans text-xs text-muted">Send this member the sign-in invitation again.</p>
      </div>

      {isAdmin && (
        <section aria-labelledby="admin-heading" className="mt-12 border-t border-border pt-8">
          <p className="font-display text-xs font-semibold uppercase tracking-[0.28em] text-accent-deep">Administration</p>
          <h2 id="admin-heading" className="mt-2 font-display text-lg font-semibold uppercase tracking-[0.1em] text-ink">Manage account</h2>

          <div className="mt-5 flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="role-select" className="block font-sans text-sm font-medium text-ink">Role</label>
              <select id="role-select" value={roleDraft} onChange={(e) => setRoleDraft(e.target.value)} disabled={busy} className={`${inputClass} mt-1.5`}>
                <option value="member">Member</option>
                <option value="board">Board</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              type="button"
              onClick={saveRole}
              disabled={busy || roleDraft === m.role}
              className="inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2.5 font-sans text-sm font-semibold text-bg transition-colors duration-150 ease-standard hover:bg-accent-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-40"
            >
              {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
              Save role
            </button>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={toggleStatus}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 font-sans text-sm font-medium text-ink transition-colors duration-150 ease-standard hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-40"
            >
              {m.status === 'active' ? 'Disable account' : 'Re-enable account'}
            </button>
            <p className="mt-1.5 font-sans text-xs text-muted">Disabling ends the member’s sessions and blocks sign-in until re-enabled.</p>
          </div>

          <div className="mt-8 border-t border-border/60 pt-6">
            {showErase ? (
              <ConfirmErase
                subject={m.displayName}
                onErase={(confirm) => eraseMember(m.id, confirm)}
                onDone={() => navigate('/members', { replace: true })}
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowErase(true)}
                className="font-sans text-sm font-medium text-error underline underline-offset-4 hover:text-error/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error"
              >
                Erase this member’s data (GDPR)…
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default function MemberDetail() {
  return <RequireRole min="board">{(viewer) => <MemberDetailInner viewer={viewer} />}</RequireRole>;
}
